<template>
    <div class="sent-email-listing">
        <TableLoading v-if="isLoading" />
        <template v-else>
            <div class="sent-email-listing__select-all">
                <CompassCheckbox
                    :modelValue="true"
                    :label="$t.__('common.actions.select_all')"
                    :checked="selectedEmails.length === emails.length"
                    :indeterminate="isCheckboxIndeterminate"
                    @change="handleCheckAll"
                />
            </div>
            <EmailListingTable
                :columns="columns"
                :emails="emails"
                :selectedEmails="selectedEmails"
                class="sent-email-listing__table"
                @open:related="handleOpenLinkObjectDialog"
                @update:checkedEmails="handleCheckEmails"
            />
        </template>
        <EmailListingPagination
            v-if="pagination.total > 0"
            :isLoading="isLoading"
            :pagination="pagination"
            :displayTotalResults="false"
            @update-pagination="updatePagination"
        />
        <EmailsListingMassActions
            :selectedEmails="selectedEmails"
            :isToolbarVisible="selectedEmails.length > 0"
            @closeToolbar="handleCloseMassActions"
            @open:related="handleOpenLinkObjectDialogFromMassActions"
        />
        <LinkObjectDialog
            :isVisible="showLinkObjectDialog"
            :related="relatedObjects"
            :isLoading="linkObjectDialogLoading"
            :hasError="hasRelatedUpdateError"
            :emptyStateData="emptyStateData"
            @close="handleCloseLinkObjectDialog"
            @update:related="updateRelatedObjects"
        />
    </div>
</template>
<script>
import EmailListingPagination from './EmailListingPagination.vue';
import EmailListingTable from 'vcomponents/email/listings/EmailListingTable.vue';
import EmailsListingMassActions from 'vcomponents/email/listings/EmailsListingMassActions.vue';
import EmailsListingMixins from 'mixins/emails/EmailsListingMixins';
import LinkedMixins from 'mixins/linked/LinkedMixins';
import LinkObjectDialog from 'vcomponents/common/object/LinkObjectDialog.vue';
import {
    searchProviderThreads,
    updateProviderThreadMessagesRelatedBatch,
} from 'api/emailEngine/emailEngineService';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';

export default {
    components: {
        EmailListingTable,
        EmailListingPagination,
        TableLoading,
        EmailsListingMassActions,
        LinkObjectDialog,
    },
    mixins: [EmailsListingMixins, LinkedMixins],
    data() {
        return {
            isLoading: false,
            columns: [
                {
                    code: 'participants',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 0,
                    sortKey: 'participants',
                },
                {
                    code: 'subject',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 1,
                    sortKey: 'subject',
                },
                {
                    code: 'related',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 2,
                    sortKey: 'related',
                },
                {
                    code: 'status',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 3,
                    sortKey: 'status',
                },
                {
                    code: 'date',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 4,
                    sortKey: 'date',
                },
            ],
            emails: [],
            currentQueryParams: {
                limit: 25,
                offset: null,
            },
            pagination: {
                limit: 25,
                total: 0,
            },
            selectedEmails: [],
            emailsToUpdate: [],
            showLinkObjectDialog: false,
            linkObjectDialogLoading: false,
            hasRelatedUpdateError: false,
        };
    },
    computed: {
        isCheckboxIndeterminate() {
            return (
                this.selectedEmails.length > 0 && this.selectedEmails.length < this.emails.length
            );
        },
        relatedObjects() {
            return this.emailsToUpdate.length === 1 ? this.emailsToUpdate[0].related : [];
        },
    },
    created() {
        this.searchProviderThreads();
    },
    methods: {
        searchProviderThreads() {
            this.isLoading = true;
            const filters = {
                folder: 'sent',
            };
            searchProviderThreads(filters, this.currentQueryParams)
                .then((response) => {
                    this.emails = response.data.map((email) => ({
                        ...email,
                        related: this.formatRelatedObjects(email.related),
                        canRead: this.canReadEmail(email._links),
                        canUpdateRelated: this.canUpdateRelated(email._links),
                        isChecked: false,
                    }));
                    this.pagination = response.pagination;
                    this.currentQueryParams = {
                        limit: response.pagination.limit,
                        offset: null,
                    };
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        updatePagination(pagination) {
            this.currentQueryParams = {
                limit: pagination.limit,
                offset: pagination.offset,
            };
            this.searchProviderThreads();
        },
        handleCheckEmails(emails) {
            this.selectedEmails = emails;
        },
        handleCheckAll() {
            this.handleCheckEmails(
                this.selectedEmails.length === this.emails.length ? [] : this.emails,
            );
        },
        handleOpenLinkObjectDialog(emailToUpdate) {
            this.emailsToUpdate.push(emailToUpdate);
            this.showLinkObjectDialog = true;
        },
        handleCloseLinkObjectDialog() {
            this.emailsToUpdate = [];
            this.selectedEmails = [];
            this.showLinkObjectDialog = false;
        },
        updateRelatedObjects(newRelatedObjects) {
            this.linkObjectDialogLoading = true;
            this.hasRelatedUpdateError = false;

            const updateProviderThreadMessagesRelatedBatchLink = this.emailsToUpdate[0]._links.find(
                (link) => link.rel === 'thread.update_related_batch',
            )?.href;

            const formatedData = this.emailsToUpdate.map((emailToUpdate) => ({
                type: 'thread',
                id: emailToUpdate.provider_thread_id,
                related:
                    this.emailsToUpdate.length === 1
                        ? this.formatAndSimplifyRelatedObjects(newRelatedObjects)
                        : this.formatAndSimplifyRelatedObjects(
                            this.mergeRelatedObjects(emailToUpdate.related, newRelatedObjects),
                        ),
            }));
            updateProviderThreadMessagesRelatedBatch(
                updateProviderThreadMessagesRelatedBatchLink,
                formatedData,
            )
                .then(() => {
                    this.emails = this.emails.map((email) => {
                        const emailToUpdate = this.emailsToUpdate.find(
                            (emailToUpdate) =>
                                emailToUpdate.provider_thread_id === email.provider_thread_id,
                        );
                        if (emailToUpdate) {
                            email.related =
                                this.emailsToUpdate.length === 1
                                    ? newRelatedObjects
                                    : this.mergeRelatedObjects(email.related, newRelatedObjects);
                        }
                        return email;
                    });

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__(
                            'email.listings.ReceivedEmailListing.success_updating_related_objects',
                        ),
                    });
                    this.handleCloseLinkObjectDialog();
                })
                .catch(() => {
                    this.hasRelatedUpdateError = true;
                })
                .finally(() => {
                    this.linkObjectDialogLoading = false;
                });
        },
        handleCloseMassActions() {
            this.emailsToUpdate = [];
            this.selectedEmails = [];
        },
        handleOpenLinkObjectDialogFromMassActions() {
            this.emailsToUpdate = this.selectedEmails;
            this.showLinkObjectDialog = true;
        },
    },
};
</script>
<style lang="scss" scoped>
.sent-email-listing {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: var(--spacing-0);
    padding: var(--spacing-3) var(--spacing-3) 0;
    background: var(--colors-common-background-default);

    &__select-all {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-shrink: 0;
        height: 48px;
        padding: 0 var(--spacing-3);
        border-width: 0px;
        border-bottom-width: 1px;
        border-style: solid;
        border-bottom-color: var(--colors-common-border-lighter);
    }

    &__table {
        flex-shrink: 1;
        overflow: auto;
    }

    &:not(&:has(.listing-settings-header)) {
        &:deep(.compass-table) {
            border-top-left-radius: var(--spacing-0);
            border-top-right-radius: var(--spacing-0);
        }
    }
}
</style>
